import SparkMD5 from 'spark-md5'
import type { UploadRequestBody } from '../../server/src/models'
import { URL_API_DOODLE, URL_API_UPLOADS, URL_DOODLE } from '../../server/src/urls'
import { dxfToSvg } from './dxfToSvg'
import { i18n } from './i18n'
import { loadPdfDocument } from './pdf'
import { canvasToFile, loadImageBlob, postJson, withDefaultErrorHandler } from './utils'

export const THUMBNAIL_WIDTH = 192
export const THUMBNAIL_HEIGHT = 128

const createDrawing = async (file: File) => {
  if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
    return file
  } else if (file.name.toLowerCase().endsWith('.dxf')) {
    const svg = dxfToSvg(await file.arrayBuffer())
    return new File([svg], file.name, { type: 'image/svg+xml' })
  } else if (file.type === 'image/bmp') {
    const image = await createImageBitmap(file)
    const canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height
    canvas.getContext('2d')!.drawImage(image, 0, 0, image.width, image.height)
    return await canvasToFile(canvas, file.name, 'image/png')
  } else {
    console.log(file)
    throw Error('Unsupported File Type')
  }
}

const createThumbnailAndCountPage = async (file: File): Promise<[File, number]> => {
  const canvas = document.createElement('canvas')
  let pageCount = 1
  if (file.type === 'application/pdf') {
    const arrayBuffer = await file.arrayBuffer()
    const doc = await loadPdfDocument(new Uint8Array(arrayBuffer))
    const page = await doc.getPage(1)
    const { width: pageWidth, height: pageHeight } = page.getViewport({ scale: 1 })
    const scale = THUMBNAIL_WIDTH / pageWidth
    canvas.width = THUMBNAIL_WIDTH
    canvas.height = Math.min(THUMBNAIL_HEIGHT, pageHeight * scale)
    await page.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport: page.getViewport({ scale }) }).promise
    pageCount = doc.numPages
  } else {
    const image = await loadImageBlob(file)
    const scale = THUMBNAIL_WIDTH / image.width
    canvas.width = THUMBNAIL_WIDTH
    canvas.height = Math.min(THUMBNAIL_HEIGHT, image.height * scale)
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(image, 0, 0, THUMBNAIL_WIDTH, image.height * scale)
  }
  return [await canvasToFile(canvas, `${file.name}.thumbnail.webp`, 'image/webp'), pageCount]
}

const handleSignedUrlUploadError = async (res: Response) => {
  try {
    const text = await res.text()
    console.error(text)
    const xml = new DOMParser().parseFromString(text, 'application/xml')
    const code = xml.getElementsByTagName('Code')[0]?.textContent
    const message = xml.getElementsByTagName('Message')[0]?.textContent
    alert(`${i18n.errorOccurred}\n\n${res.status} ${code ?? ''}\n${message ?? text}`)
  } catch {
    alert(`${i18n.errorOccurred}\n\n${res.status}`)
  }
}

export const uploadFile = async (file: File, parentFolderId: string, options?: { sourceDoodleId?: string; blackedOut?: 1 }) => {
  const drawing = await createDrawing(file)
  const [thumbnail, pageCount] = await createThumbnailAndCountPage(drawing)
  const md5 = btoa(new SparkMD5.ArrayBuffer().append(await drawing.arrayBuffer()).end(true))

  // 署名付き URL を発行します。
  const res = await withDefaultErrorHandler(
    postJson<UploadRequestBody>(URL_API_UPLOADS, {
      filename: drawing.name,
      filesize: drawing.size,
      contentType: drawing.type,
      md5,
      parentFolderId,
      sourceDoodleId: options?.sourceDoodleId ? options.sourceDoodleId : undefined,
      pageCount,
      blackedOut: options?.blackedOut ? 1 : undefined,
    }),
  )
  if (!res) {
    return
  }
  const { id, url } = (await res.json()) as { id: string; url: string }

  // 図面を GCS へアップロードします。
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': drawing.type, 'Content-MD5': md5, 'X-Upload-Content-Length': String(drawing.size) },
      body: drawing,
    })
    if (!res.ok) {
      await handleSignedUrlUploadError(res)
      return
    }
  } catch {
    alert(i18n.networkErrorOccurred)
    return
  }

  // サムネイルをアップロードします。
  {
    const body = new FormData()
    body.append('thumbnail', thumbnail)
    const res = await withDefaultErrorHandler(fetch(URL_API_DOODLE(id), { method: 'POST', body }))
    if (!res) {
      return
    }
  }

  return URL_DOODLE(id)
}
